import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Tooltip, Modal, Button, Typography } from 'antd';
import classNames from 'classnames';
import Icon, { UpOutlined, DownOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { MessagingChannelFeature, Permissions, WhatsApp } from '@whatsper/texterchat-common';
import { BsChatRightTextFill } from 'react-icons/bs';
import ActiveIcon from '../ActiveIcon';
import HightLayout from '../HightLayout';
import * as constants from '../../../constants';
import { ReactComponent as LeftArrowQuickReplies } from '../../../assets/icons/arrows/quickRepliesLeftArrow.svg';
import { ReactComponent as QuickRepliesGear } from '../../../assets/icons/leftMenuIcons/settingsOutlined.svg';
import { ReactComponent as QuickRepliesPlus } from '../../../assets/icons/plus/quickRepliesPlus.svg';
import { ReactComponent as QuickRepliesFolder } from '../../../assets/icons/file/folderIcon.svg';
import { ReactComponent as BotIcon } from '../../../assets/icons/leftMenuIcons/botActive.svg';
import { useStore } from '../../../store/rootStore';
import { TEMPLATE_USAGE } from '../../../constants';
import { filterQuickRepliesForChat, hasUnseenAiReply, isAiQuickReply, markSeen, } from '../../../helpers/aiQuickReplies';
import { getDirection } from '../../../helpers';
import QuickRepliesMenu from './QuickReplies';
import QuickReplies from '../../QuickReplies';
import TemplateMessages from '../../TemplateMessages';
import TemplateMessagesMenu from './TemplateMessages';
import './PreparedMessages.scss';
const { Text } = Typography;
const PreparedMessages = ({ onQuickReplySelected }) => {
    var _a, _b;
    const { activeChatStore, loginStore, templateMessagesStore, quickRepliesStore, channels, agentsStore } = useStore();
    const { t } = useTranslation();
    const direction = getDirection();
    /** State to control the visibility of the prepared messages */
    const [showPreparedMessages, setShowPreparedMessages] = useState(false);
    const [showQuickReplyMenu, setShowQuickReplyMenu] = useState(false);
    const [showTemplateMessagesMenu, setShowTemplateMessagesMenu] = useState(false);
    /* Templates */
    const [templatesSupported, setTemplatesSupported] = useState(false);
    const channelInfo = (_a = activeChatStore.chat) === null || _a === void 0 ? void 0 : _a.channelInfo;
    const activeChatId = (_b = activeChatStore.chat) === null || _b === void 0 ? void 0 : _b._id;
    const visibleAiReplies = filterQuickRepliesForChat(quickRepliesStore.quickReplies, activeChatStore.chat).filter(isAiQuickReply);
    const aiRepliesKey = visibleAiReplies
        .map((r) => { var _a, _b, _c, _d; return `${r._id}:${(_d = (_b = (_a = r.metadata) === null || _a === void 0 ? void 0 : _a.updated) !== null && _b !== void 0 ? _b : (_c = r.metadata) === null || _c === void 0 ? void 0 : _c.created) !== null && _d !== void 0 ? _d : 0}`; })
        .join(',');
    const hasAiAnswer = !activeChatStore.sessionIsOver && visibleAiReplies.length > 0;
    const hasUnseenAi = hasAiAnswer && !!activeChatId && hasUnseenAiReply(activeChatId, visibleAiReplies);
    useEffect(() => {
        if (showPreparedMessages && activeChatId && visibleAiReplies.length > 0) {
            markSeen(activeChatId, visibleAiReplies);
        }
    }, [showPreparedMessages, activeChatId, aiRepliesKey]);
    const handleOpen = () => {
        setShowPreparedMessages(!showPreparedMessages);
        templateMessagesStore.setStoreToDefault();
    };
    const closeAllModals = (e) => {
        setShowPreparedMessages(false);
        setShowQuickReplyMenu(false);
        setShowTemplateMessagesMenu(false);
    };
    const handleOpenModalMore = () => {
        setShowQuickReplyMenu(!showQuickReplyMenu);
        setShowPreparedMessages(false);
    };
    const checkIfDisabled = () => {
        if (!activeChatStore.chat || !loginStore.userDetails)
            return false;
        return (activeChatStore.chat.status === constants.CHAT_STATUS.ASSIGNED &&
            activeChatStore.chat.agent.uid === loginStore.userDetails._id);
    };
    useEffect(() => {
        window.addEventListener('popstate', closeAllModals);
        return () => {
            window.removeEventListener('popstate', closeAllModals);
        };
    }, []);
    /* Check if the channel supports templates */
    useEffect(() => {
        const adapter = channelInfo
            ? channels.getAccountAdapter({
                id: channelInfo.accountId,
                name: channelInfo.name,
            })
            : null;
        const supportsTemplates = channels.hasFeatures([MessagingChannelFeature.templates], adapter);
        setTemplatesSupported(supportsTemplates);
    }, [channelInfo]);
    const renderPreparedMessagesList = () => {
        if (!activeChatStore.sessionIsOver && quickRepliesStore.quickReplies.length) {
            return (React.createElement(QuickReplies, { items: quickRepliesStore.quickReplies, loading: quickRepliesStore.quickRepliesLoading, onClicked: (reply) => {
                    handleOpen();
                    onQuickReplySelected(reply);
                }, onClose: handleOpen }));
        }
        if (activeChatStore.sessionIsOver && !!templateMessagesStore.templateMessagesList.length) {
            return (React.createElement(TemplateMessages, { templates: templateMessagesStore.templateMessagesList, loading: templateMessagesStore.templateMessagesLoading, onClicked: (tpl) => {
                    handleOpen();
                    templateMessagesStore.setTemplateEdited(tpl);
                    setShowTemplateMessagesMenu(true);
                }, onClose: () => setShowPreparedMessages(false), onlyUsage: TEMPLATE_USAGE.INBOX, onlyStatus: [WhatsApp.TemplateLocalizationStatus.APPROVED] }));
        }
        return (React.createElement("div", { className: 'noRepliesContainer' },
            React.createElement("div", { className: 'noRepliesBody' },
                React.createElement(Icon, { className: 'noRepliesIcon', component: QuickRepliesFolder }),
                React.createElement("div", { className: 'noRepliesBodyText' },
                    React.createElement(Text, { strong: true },
                        React.createElement(Trans, { i18nKey: activeChatStore.sessionIsOver ? 'didntCreateTemplates' : 'didntCreateQuickReplies' })))),
            activeChatStore.sessionIsOver ? ('') : (React.createElement(Tooltip, { className: 'editReplyContainer', overlayStyle: { zIndex: 989 }, title: t("addOrEditQuickReply") },
                React.createElement(Button, { onClick: handleOpenModalMore, className: 'addReplyButton' },
                    React.createElement(Icon, { component: QuickRepliesPlus }),
                    React.createElement(Text, { strong: true },
                        React.createElement(Trans, { i18nKey: "createNewQuickReply" })))))));
    };
    if (activeChatStore.sessionIsOver && !templatesSupported)
        return null;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "preparedMessagesContainer", dir: direction }, activeChatStore.chat ? (React.createElement(HightLayout, { isActive: showPreparedMessages, onClick: () => setShowPreparedMessages(false) },
            React.createElement("div", { className: 'quickRepliesBarWithSyncButton' },
                React.createElement("div", { className: classNames('quickRepliesMainField', {
                        ['quickRepliesDisabled']: !checkIfDisabled(),
                    }) },
                    React.createElement("div", { className: 'quickRepliesTitleHolder', onClick: checkIfDisabled()
                            ? () => {
                                handleOpen();
                            }
                            : undefined },
                        React.createElement("div", { className: 'quickRepliesTitleLeft' },
                            activeChatStore.chat &&
                                (activeChatStore.chat.status !== constants.CHAT_STATUS.ASSIGNED ||
                                    activeChatStore.sessionIsOver) ? (React.createElement(BsChatRightTextFill, { className: 'coloredIcon' })) : (React.createElement(Icon, { component: LeftArrowQuickReplies, className: "coloredIcon quickRepliesDirectionIcon" })),
                            React.createElement(Text, { strong: true, className: 'titleQuickReplies' }, t(activeChatStore.sessionIsOver ? 'messageTemplates' : 'quickReplies')),
                            hasAiAnswer && (React.createElement("span", { className: classNames('aiAnswerPill', { hasUnseen: hasUnseenAi }), onClick: (e) => {
                                    e.stopPropagation();
                                    if (checkIfDisabled())
                                        handleOpen();
                                } },
                                React.createElement(Icon, { component: BotIcon, className: "aiAnswerIcon" }),
                                React.createElement("span", { className: "aiAnswerLabelFull" },
                                    React.createElement(Trans, { i18nKey: "aiAnswerAvailable" })),
                                React.createElement("span", { className: "aiAnswerLabelShort" },
                                    React.createElement(Trans, { i18nKey: "aiAnswerShort" })),
                                hasUnseenAi && React.createElement("span", { className: "aiAnswerDot" })))),
                        showPreparedMessages ? React.createElement(UpOutlined, null) : React.createElement(DownOutlined, null)),
                    agentsStore.hasPermissions([Permissions.Permission.quick_replies_manage]) && (React.createElement("div", { className: 'quickRepliesTitleRight', onClick: () => {
                            if (!activeChatStore.sessionIsOver) {
                                handleOpenModalMore();
                            }
                            else {
                                templateMessagesStore.toggleNewTemplateDrawer();
                            }
                        } },
                        React.createElement(ActiveIcon, { component: QuickRepliesGear })))),
                showPreparedMessages && React.createElement("div", { className: "quickReplies" }, renderPreparedMessagesList())))) : null),
        React.createElement(Modal, { open: showTemplateMessagesMenu, onCancel: () => {
                templateMessagesStore.setStoreToDefault();
                setShowTemplateMessagesMenu(!showTemplateMessagesMenu);
            }, bodyStyle: {
                padding: '0',
                boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.2)',
                borderRadius: '2px',
                minWidth: '60vw',
            }, footer: null, closable: false, width: "60vw", centered: true },
            React.createElement(TemplateMessagesMenu, { hideMenu: () => {
                    setShowTemplateMessagesMenu(false);
                    templateMessagesStore.setStoreToDefault();
                } })),
        React.createElement(Modal, { open: showQuickReplyMenu && !!activeChatStore.chat && !activeChatStore.sessionIsOver, onCancel: handleOpenModalMore, bodyStyle: {
                padding: '0',
                boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.2)',
                borderRadius: '2px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }, footer: null, closable: false, width: 800, centered: true, destroyOnClose: true },
            React.createElement(QuickRepliesMenu, { closeMenu: () => setShowQuickReplyMenu(false), noQuickReplies: quickRepliesStore.quickReplies.length === 0, channelInfo: channelInfo })),
        showPreparedMessages ? React.createElement("div", { className: "quickRepliesDarkBackground", onClick: handleOpen }) : null));
};
/**
 * PreparedMessages Component
 *
 * @description Renders either {@link QuickRepliesMenu} or {@link TemplateMessagesMenu}
 * @param {Function} onQuickReplySelected - Function to handle quick reply selection.
 * @returns {JSX.Element} The rendered component.
 */
export default observer(PreparedMessages);
